import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ShopContext = createContext(null);

const CART_KEY = "kubwalo_cart";

function loadCart() {
  try {
    return JSON.parse(localStorage.getItem(CART_KEY)) || [];
  } catch {
    return [];
  }
}

export function ShopProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState(loadCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({}));
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = useCallback((product, sizeLabel, quantity = 1, silent = false) => {
    const size = product.sizes.find((s) => s.label === sizeLabel) || product.sizes[0];
    if (!size) return;
    setCart((prev) => {
      const idx = prev.findIndex((i) => i.product_id === product.id && i.size_label === size.label);
      const next = [...prev];
      if (idx >= 0) {
        const newQty = Math.min(next[idx].quantity + quantity, size.stock || 99);
        next[idx] = { ...next[idx], quantity: newQty };
      } else {
        const itemImage = size.label.toLowerCase().includes("roll")
          ? (product.rollon_image_url || product.image_url || "")
          : (product.image_url || "");
        next.push({
          product_id: product.id,
          name: product.name,
          size_label: size.label,
          price: size.price,
          image_url: itemImage,
          quantity: Math.min(quantity, size.stock || 99),
          max_stock: size.stock,
        });
      }
      return next;
    });
    if (!silent) {
      toast.success(`${product.name} (${size.label}) added to your bag`, { id: `add-${product.id}-${size.label}` });
    }
    setCartOpen(true);
  }, []);

  const updateQuantity = useCallback((product_id, size_label, quantity) => {
    setCart((prev) =>
      quantity <= 0
        ? prev.filter((i) => !(i.product_id === product_id && i.size_label === size_label))
        : prev.map((i) =>
            i.product_id === product_id && i.size_label === size_label
              ? { ...i, quantity: Math.min(quantity, i.max_stock || 99) }
              : i
          )
    );
  }, []);

  const removeItem = useCallback((product_id, size_label) => {
    setCart((prev) => prev.filter((i) => !(i.product_id === product_id && i.size_label === size_label)));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = useMemo(() => cart.reduce((sum, i) => sum + i.quantity, 0), [cart]);
  const cartSubtotal = useMemo(() => cart.reduce((sum, i) => sum + i.price * i.quantity, 0), [cart]);

  const value = {
    settings, cart, cartCount, cartSubtotal,
    addToCart, updateQuantity, removeItem, clearCart,
    cartOpen, setCartOpen, searchOpen, setSearchOpen,
  };
  return <ShopContext.Provider value={value}>{children}</ShopContext.Provider>;
}

export function useShop() {
  return useContext(ShopContext);
}
